import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getTranslationStatuses,
  getTranslationOverview,
  getTranslationNarratives,
  updateNarrativeTranslationStatusBulk,
  importTranslationXml,
  exportTranslationXml,
  generateReport,
  getDocumentStatus,
  downloadArtifacts,
  approveReports,
  exportTranslationToExcel,
  getReportTypes,
  getIsoLanguages,
  getFundsList,
  getReportingPeriods,
} from 'Services/overviewApi';
import classes from './Translation.module.scss';

const STATUS_BADGE_MAP = {
  1: { label: 'New', cls: classes.statusNew },
  2: { label: 'Ready', cls: classes.statusNew },
  3: { label: 'In Progress', cls: classes.statusInProgress },
  4: { label: 'Completed', cls: classes.statusCompleted },
  90: { label: 'Generated', cls: classes.statusGenerated },
  100: { label: 'Approved', cls: classes.statusApproved },
};

const POLL_INTERVAL = 5000;

const Translation = () => {
  const context = useContext(AppContext);
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('status'); // 'status' | 'text'

  // Filters
  const [fundCode, setFundCode] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedReportType, setSelectedReportType] = useState('');
  const [languageReference, setLanguageReference] = useState('TARGET');

  // Lookups
  const [translationStatuses, setTranslationStatuses] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [languages, setLanguages] = useState([]);

  // Data
  const [overviewData, setOverviewData] = useState([]);
  const [narrativesData, setNarrativesData] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set()); // "FundCode|IsoLanguage" keys
  const [selectedNarrativeIds, setSelectedNarrativeIds] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);

  // Modals
  const [activeModal, setActiveModal] = useState(null);
  const [changeStatusValue, setChangeStatusValue] = useState('');

  // Dropdown menus
  const [openDropdown, setOpenDropdown] = useState(null); // 'generate' | 'download' | 'approve' | null
  const dropdownRef = useRef(null);

  const reportingPeriod = context.state?.selectedReportingDate?.code;
  const regulation = context.state?.selectedRegulation;
  const userEmail = context.state?.userEmail || 'Unknown';

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load lookups
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [statusResult, rtResult, langResult] = await Promise.all([
          getTranslationStatuses(),
          getReportTypes(),
          getIsoLanguages(),
        ]);
        setTranslationStatuses(statusResult.value || statusResult.Content || []);
        setReportTypes(rtResult.value || rtResult.Content || []);
        setLanguages(langResult.value || langResult.Content || []);
      } catch (err) {
        context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load lookups'}</> });
      }
    };
    loadLookups();
  }, [context.handlers]);

  // Load overview data
  const loadData = useCallback(async () => {
    if (!reportingPeriod || !regulation) {
      setLoading(false);
      setOverviewData([]);
      return;
    }
    try {
      setLoading(true);
      const result = await getTranslationOverview(reportingPeriod, regulation.Name, fundCode, '');
      const data = result.value || result.Content || [];
      setOverviewData(data);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load translation data'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod, regulation, fundCode, context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load narratives for text view
  const loadNarratives = useCallback(async () => {
    if (!reportingPeriod || !regulation || viewMode !== 'text') return;
    try {
      const result = await getTranslationNarratives(reportingPeriod, regulation.Name);
      const data = result.value || result.Content || [];
      setNarrativesData(data);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load narratives'}</> });
    }
  }, [reportingPeriod, regulation, viewMode, context.handlers]);

  useEffect(() => {
    loadNarratives();
  }, [loadNarratives]);

  // Cleanup poll timer
  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  // Build fund cards from overview data
  const fundCards = useMemo(() => {
    const map = new Map();
    overviewData.forEach((item) => {
      if (!map.has(item.fundcode)) {
        map.set(item.fundcode, { FundCode: item.fundcode, FundName: item.fundname, FundClassCount: item.fundclasscount, languages: [] });
      }
      item.LanguageName = item.englishlanguagename;
      item.IsoLanguage = item.classlanguage;
      item.IsoBaseLanguage = item.baselanguage;
      item.IsoTargetLanguage = item.classlanguage;
      item.FundClassCount = item.fundclasscount;
      item.TotalTranslationItems = item.totalnarrativesforfund;
      item.TranslatedTranslationItems = item.totaltranslatednarrativesforisolanguage;
      item.ValidatedTranslationItems = item.totalapprovednarrativesforisolanguage;
      item.ClassCount = item.classcount;
      map.get(item.fundcode).languages.push(item);
    });
    let cards = Array.from(map.values());

    // Apply filters
    if (selectedLanguage) {
      cards = cards
        .map((c) => ({
          ...c,
          languages: c.languages.filter((l) => l.IsoLanguage === selectedLanguage),
        }))
        .filter((c) => c.languages.length > 0);
    }
    if (selectedStatus) {
      cards = cards
        .map((c) => ({
          ...c,
          languages: c.languages.filter((l) => String(l.TranslationStatusId) === selectedStatus),
        }))
        .filter((c) => c.languages.length > 0);
    }
    return cards;
  }, [overviewData, selectedLanguage, selectedStatus]);

  // Filtered narratives for text view
  const filteredNarratives = useMemo(() => {
    // Map lowercase API fields to PascalCase expected by grid columns
    let data = narrativesData.map((n) => ({
      ...n,
      BaseNarrativeKey: n.basenarrativekey ?? n.BaseNarrativeKey,
      BaseNarrativeId: n.basenarrativeid ?? n.BaseNarrativeId,
      BaseNarrativeValue: n.basenarrativevalue ?? n.BaseNarrativeValue,
      TargetNarrativeValue: n.targetnarrativevalue ?? n.TargetNarrativeValue ?? '',
      IsoBaseLanguage: n.isobaselanguage ?? n.IsoBaseLanguage,
      IsoTargetLanguage: n.isotargetlanguage ?? n.IsoTargetLanguage,
      TargetTranslationStatusName: n.targettranslationstatusname ?? n.basetranslationstatusname ?? n.TargetTranslationStatusName ?? '',
      TranslationStatusId: n.targettranslationstatusid ?? n.basetranslationstatusid ?? n.TranslationStatusId,
      FundCode: n.fundcode ?? n.FundCode ?? '',
      FundName: n.fundname ?? n.FundName ?? '',
      ClassCode: n.classcode ?? n.ClassCode ?? '',
      ReportTypes: n.serializedreports ?? n.ReportTypes ?? '',
    }));

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (n) =>
          n.BaseNarrativeKey?.toLowerCase().includes(q) ||
          n.BaseNarrativeValue?.toLowerCase().includes(q) ||
          n.FundCode?.toLowerCase().includes(q),
      );
    }
    if (selectedLanguage) {
      data = data.filter((n) =>
        languageReference === 'TARGET'
          ? n.IsoTargetLanguage === selectedLanguage
          : n.IsoBaseLanguage === selectedLanguage,
      );
    }
    if (selectedStatus) data = data.filter((n) => String(n.TranslationStatusId) === selectedStatus);
    if (selectedReportType) data = data.filter((n) => (n.ReportTypes || '').includes(selectedReportType));
    return data;
  }, [narrativesData, searchText, selectedLanguage, selectedStatus, selectedReportType, languageReference]);

  const toggleCardSelection = useCallback((fundCode, isoLanguage) => {
    const key = `${fundCode}|${isoLanguage}`;
    setSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleFundSelection = useCallback((fundCode, fundLanguages, shouldSelect) => {
    setSelectedCards((prev) => {
      const next = new Set(prev);
      fundLanguages.forEach((lang) => {
        const key = `${fundCode}|${lang.IsoLanguage}`;
        if (shouldSelect) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, []);

  // ─── Actions ────────────────────────────────────────────────────────

  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      await importTranslationXml(file, file.name);
      context.handlers.setToast({ type: 'success', body: <>XML imported successfully</> });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Import failed'}</> });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  }, [context.handlers, loadData]);

  const handleExport = useCallback(async () => {
    if (selectedCards.size === 0) {
      context.handlers.setToast({ type: 'error', body: <>Select at least one fund/language combination</> });
      return;
    }

    try {
      setLoading(true);

      // Group by language pairs
      const exportItems = [...selectedCards].map((key) => {
        const [fc, iso] = key.split('|');
        return { FundCode: fc, IsoLanguage: iso };
      });

      const data = await exportTranslationXml({
        Items: exportItems,
        ReportingPeriod: reportingPeriod,
        Regulation: regulation,
      });

      // Download the returned bytes as XML
      const blob = new Blob([data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'translations_export.xml';
      a.click();
      URL.revokeObjectURL(url);

      // Set status to "Translation in Progress" (3)
      const narrativeIds = overviewData
        .filter((o) => selectedCards.has(`${o.FundCode}|${o.IsoLanguage}`))
        .map((o) => o.NarrativeId)
        .filter(Boolean);
      if (narrativeIds.length > 0) {
        await updateNarrativeTranslationStatusBulk(narrativeIds, 3, userEmail);
      }

      context.handlers.setToast({ type: 'success', body: <>XML exported successfully</> });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Export failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [selectedCards, overviewData, reportingPeriod, regulation, userEmail, context.handlers, loadData]);

  const handleExportExcel = useCallback(async () => {
    try {
      setLoading(true);
      const data = await exportTranslationToExcel(reportingPeriod, regulation, fundCode, '');

      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Translations_${reportingPeriod}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Excel export failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod, regulation, fundCode, context.handlers]);

  const handleChangeStatus = useCallback(async () => {
    if (!changeStatusValue) return;
    const ids = viewMode === 'status'
      ? overviewData
          .filter((o) => selectedCards.has(`${o.FundCode}|${o.IsoLanguage}`))
          .map((o) => o.NarrativeId)
          .filter(Boolean)
      : [...selectedNarrativeIds];

    if (ids.length === 0) {
      context.handlers.setToast({ type: 'error', body: <>Select items first</> });
      return;
    }

    try {
      setLoading(true);
      await updateNarrativeTranslationStatusBulk(ids, Number(changeStatusValue), userEmail);
      context.handlers.setToast({ type: 'success', body: <>Status updated</> });
      setActiveModal(null);
      setSelectedCards(new Set());
      setSelectedNarrativeIds(new Set());
      await loadData();
      if (viewMode === 'text') await loadNarratives();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Status update failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [changeStatusValue, viewMode, overviewData, selectedCards, selectedNarrativeIds, userEmail, context.handlers, loadData, loadNarratives]);

  const handleGenerate = useCallback(async () => {
    if (selectedCards.size === 0) {
      context.handlers.setToast({ type: 'error', body: <>Select fund/language combinations to generate</> });
      return;
    }

    try {
      setLoading(true);
      const items = [...selectedCards].map((key) => {
        const [fc, iso] = key.split('|');
        return { FundCode: fc, IsoLanguage: iso };
      });

      await generateReport({
        Items: items,
        ReportingPeriod: reportingPeriod,
        Regulation: regulation,
      });

      setIsGenerating(true);
      context.handlers.setToast({ type: 'success', body: <>Generation started</> });

      // Start polling
      pollTimerRef.current = setInterval(async () => {
        try {
          const statusResult = await getDocumentStatus({
            Items: items,
            ReportingPeriod: reportingPeriod,
            Regulation: regulation,
          });
          const statuses = statusResult.value || statusResult.Content || statusResult;

          // Update overview data with generation statuses
          setOverviewData((prev) =>
            prev.map((item) => {
              const status = Array.isArray(statuses)
                ? statuses.find(
                    (s) => s.FundCode === item.FundCode && s.IsoLanguage === item.IsoLanguage,
                  )
                : null;
              if (status) {
                return { ...item, DocumentStatusId: status.DocumentStatusId, DocumentStatusName: status.DocumentStatusName };
              }
              return item;
            }),
          );

          // Check if all are done (90=Generated or 100=Approved)
          const allDone = Array.isArray(statuses) && statuses.every(
            (s) => s.DocumentStatusId === 90 || s.DocumentStatusId === 100,
          );
          if (allDone) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            setIsGenerating(false);
            await loadData();
          }
        } catch {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setIsGenerating(false);
        }
      }, POLL_INTERVAL);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Generation failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [selectedCards, reportingPeriod, regulation, context.handlers, loadData]);

  const handleDownload = useCallback(async () => {
    if (selectedCards.size === 0) {
      context.handlers.setToast({ type: 'error', body: <>Select fund/language combinations to download</> });
      return;
    }

    try {
      setLoading(true);
      const items = [...selectedCards].map((key) => {
        const [fc, iso] = key.split('|');
        return { FundCode: fc, IsoLanguage: iso };
      });

      const data = await downloadArtifacts({
        Items: items,
        ReportingPeriod: reportingPeriod,
        Regulation: regulation,
      });

      const blob = new Blob([data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Documents_${reportingPeriod}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Download failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [selectedCards, reportingPeriod, regulation, context.handlers]);

  const handleApprove = useCallback(async () => {
    if (selectedCards.size === 0) {
      context.handlers.setToast({ type: 'error', body: <>Select fund/language combinations to approve</> });
      return;
    }

    try {
      setLoading(true);
      const items = [...selectedCards].map((key) => {
        const [fc, iso] = key.split('|');
        return { FundCode: fc, IsoLanguage: iso };
      });

      await approveReports(items);
      context.handlers.setToast({ type: 'success', body: <>Reports approved</> });
      setSelectedCards(new Set());
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Approval failed'}</> });
    } finally {
      setLoading(false);
    }
  }, [selectedCards, context.handlers, loadData]);

  // Narratives grid columns
  const narrativeColumnDefs = useMemo(() => [
    {
      headerName: '',
      width: 50,
      sortable: false,
      filter: false,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    { headerName: 'ID', field: 'BaseNarrativeKey', width: 180, filter: 'agTextColumnFilter' },
    {
      headerName: 'Reference',
      field: 'BaseNarrativeId',
      width: 220,
      filter: 'agTextColumnFilter',
      cellStyle: { whiteSpace: 'normal', lineHeight: '18px', paddingTop: '2px', paddingBottom: '2px', overflow: 'visible' },
      cellRenderer: (params) => {
        const { FundCode, ClassCode, FundName } = params.data;
        if (!FundCode && !ClassCode) {
          return <span><b>Template</b></span>;
        }
        if (!ClassCode) {
          return <span><b>Fund</b><br />{FundName} ({FundCode})</span>;
        }
        return <span><b>Share</b><br />{FundName} ({ClassCode})</span>;
      },
    },
    { headerName: 'B', field: 'IsoBaseLanguage', width: 50, filter: 'agTextColumnFilter' },
    { headerName: 'T', field: 'IsoTargetLanguage', width: 50, filter: 'agTextColumnFilter' },
    { headerName: 'Narrative', field: 'BaseNarrativeValue', flex: 1, filter: 'agTextColumnFilter' },
    { headerName: 'Translated Narrative', field: 'TargetNarrativeValue', flex: 1, filter: 'agTextColumnFilter' },
    { headerName: 'Translation Status', field: 'TargetTranslationStatusName', width: 150, filter: 'agTextColumnFilter' },
  ], []);

  const getStatusBadge = (statusId) => {
    const badge = STATUS_BADGE_MAP[statusId];
    if (!badge) return null;
    return <span className={`${classes.statusBadge} ${badge.cls}`}>{badge.label}</span>;
  };

  if (loading && overviewData.length === 0) return <GrpSpinner />;

  return (
    <div className={classes.container}>
      <input
        type="file"
        ref={fileInputRef}
        className={classes.hiddenFileInput}
        accept=".xml"
        onChange={handleFileSelected}
      />

      {/* Toolbar */}
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <input
            className={classes.searchInput}
            placeholder="Search fund..."
            value={fundCode}
            onChange={(e) => setFundCode(e.target.value)}
          />
          {viewMode === 'text' && (
            <input
              className={classes.searchInput}
              placeholder="Search narratives..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          )}
        </div>
        <div className={classes.toolbarRight}>
          <div className={classes.viewToggle}>
            <button
              className={`${classes.viewToggleBtn} ${viewMode === 'status' ? classes.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('status')}
            >
              Status View
            </button>
            <button
              className={`${classes.viewToggleBtn} ${viewMode === 'text' ? classes.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('text')}
            >
              Text View
            </button>
          </div>
          {isGenerating && <span className={classes.generatingIndicator}>Generating...</span>}
        </div>
      </div>

      {/* Filters */}
      <div className={classes.filterRow}>
        <div className={classes.filterGroup}>
          <label htmlFor="referenceSelect">Reference:</label>
          <select id="referenceSelect" value={languageReference} onChange={(e) => setLanguageReference(e.target.value)}>
            <option value="BASE">Base language</option>
            <option value="TARGET">Target language</option>
          </select>
        </div>
        <div className={classes.filterGroup}>
          
          <label htmlFor="languageSelect">Language:</label>
          <select id="languageSelect" value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
            <option value="">All</option>
            {languages.map((l) => {
              l.IsoLanguage=l.isolanguage;
              l.LanguageName=l.englishlanguagename;
              return <option key={l.IsoLanguage} value={l.IsoLanguage}>{l.LanguageName || l.IsoLanguage}</option>;
            })}
          </select>
        </div>
        <div className={classes.filterGroup}>
          <label htmlFor="statusSelect">Status:</label>
          <select id="statusSelect" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="">All</option>
            {translationStatuses.map((s) => (
              <option key={s.TranslationStatusId} value={s.TranslationStatusId}>
                {s.DisplayName}
              </option>
            ))}
          </select>
        </div>
        {viewMode === 'text' && (
          <div className={classes.filterGroup}>
            <label htmlFor="reportTypeSelect">Report Type:</label>
            <select id="reportTypeSelect" value={selectedReportType} onChange={(e) => setSelectedReportType(e.target.value)}>
              <option value="">All</option>
              {reportTypes.map((rt) => (
                <option key={rt.ReportTypeId} value={rt.ReportTypeId}>{rt.ReportTypeName}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Action buttons */}

        <div className={classes.actionBar} ref={dropdownRef}>
          <GrpButton size="Small" type="Secondary" text="Import" icon={['fas', 'upload']} onClick={handleImport} />
          {viewMode === 'text' ? (
          <>
          <GrpButton size="Small" type="Secondary" text="Export XML" icon={['fas', 'download']} onClick={handleExport} />
          <GrpButton size="Small" type="Secondary" text="Export Excel" icon={['fas', 'file-excel']} onClick={handleExportExcel} />
          <GrpButton
            size="Small"
            type="Secondary"
            text="Change Status"
            icon={['fas', 'exchange-alt']}
            onClick={() => { setChangeStatusValue(''); setActiveModal('changeStatus'); }}
          /></>
          ):(
            <>
          {/* Generate dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton size="Small" type="Secondary" text="Generate ▾" icon={['fas', 'play']} onClick={() => setOpenDropdown(openDropdown === 'generate' ? null : 'generate')} />
            {openDropdown === 'generate' && (
              <div className={classes.dropdownMenu}>
                <div className={classes.dropdownGroupLabel}>KID</div>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleGenerate(); }}>
                  Generate Funds and Languages ({selectedCards.size})
                </button>
              </div>
            )}
          </div>
          {/* Download dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton size="Small" type="Secondary" text="Download ▾" icon={['fas', 'cloud-download-alt']} onClick={() => setOpenDropdown(openDropdown === 'download' ? null : 'download')} />
            {openDropdown === 'download' && (
              <div className={classes.dropdownMenu}>
                <div className={classes.dropdownGroupLabel}>KID</div>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleDownload(); }}>
                  KID - Selected Fund(s)/Languages ({selectedCards.size})
                </button>
              </div>
            )}
          </div>
          {/* Approve dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton size="Small" type="Primary" text="Approve ▾" icon={['fas', 'check']} onClick={() => setOpenDropdown(openDropdown === 'approve' ? null : 'approve')} />
            {openDropdown === 'approve' && (
              <div className={classes.dropdownMenu}>
                <div className={classes.dropdownGroupLabel}>KID</div>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleApprove(); }}>
                  KID - Selected Fund(s)/Languages ({selectedCards.size})
                </button>
              </div>
            )}
          </div>
            </>
          )}

        </div>


      {/* Content */}
      <div className={classes.content}>
        {viewMode === 'status' ? (
          <div className={classes.carouselContainer}>
            {fundCards.map((fund) => {
              const selectedLanguagesCount = fund.languages.reduce(
                (count, lang) => (selectedCards.has(`${fund.FundCode}|${lang.IsoLanguage}`) ? count + 1 : count),
                0,
              );
              const isTranslationComplete = !fund.languages.some(
                (l) => l.ValidatedTranslationItems < l.TotalTranslationItems,
              );
              const allLanguagesSelected = fund.languages.length > 0 && selectedLanguagesCount === fund.languages.length;
              const someLanguagesSelected = selectedLanguagesCount > 0 && !allLanguagesSelected;
              return (
                <div key={fund.FundCode} className={classes.fundCardRow}>
                  <div
                    className={`k-card ${classes.fundCardColorIndicator} ${isTranslationComplete ? classes.fundCardColorIndicatorComplete : classes.fundCardColorIndicatorIncomplete}`}
                  />
                  <div className={classes.fundCard}>
                    <div className={classes.fundCardHeader}>
                    <input
                      type="checkbox"
                      className={classes.checkbox}
                      style={{marginRight:"0.5em"}}
                      checked={allLanguagesSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someLanguagesSelected;
                      }}
                      onChange={(e) => toggleFundSelection(fund.FundCode, fund.languages, e.target.checked)}
                    />
                    <span>{fund.FundName}</span>
                    <br />
                    <span>{fund.FundCode + " (" + fund.FundClassCount + " classes)"}</span>
                    </div>
                    <div className={classes.fundCardBody}>
                      {fund.languages.map((lang) => {
                        console.log(lang);
                        console.log(fund);
                        const key = `${fund.FundCode}|${lang.IsoLanguage}`;
                        let languageStatus = "Incomplete";
                        if(lang.IsoBaseLanguage === lang.IsoTargetLanguage){
                          languageStatus = lang.ValidatedTranslationItems == lang.TotalTranslationItems ? "Approved" : "Complete";
                        }else{
                          languageStatus = (lang.ValidatedTranslationItems == lang.TotalTranslationItems) ? "Approved" : (lang.TranslatedTranslationItems == lang.TotalTranslationItems ? "Complete" : "Incomplete");
                        }
                        return (
                          <div key={key} className={classes.langRow}>
                            <div className={classes.langRowLeft}>
                              <input
                                type="checkbox"
                                className={classes.checkbox}
                                checked={selectedCards.has(key)}
                                onChange={() => toggleCardSelection(fund.FundCode, lang.IsoLanguage)}
                              />
                              <div className={classes.langInfo}>
                                <span className={classes.langName}>{lang.LanguageName || lang.IsoLanguage}</span>
                                <span className={classes.langStatus}>{languageStatus}</span>
                              </div>
                              {lang.IsoBaseLanguage === lang.IsoTargetLanguage && (
                                <span className={classes.baseBadge}>Base</span>
                              )}
                              <span className={classes.classCountBadge}>{lang.ClassCount ?? 0}</span>
                            </div>
                            <span className={classes.narrativeCountBadge}>
                              {lang.totalapprovednarrativesforisolanguage ?? 0}/{lang.totalnarrativesforfund ?? 0}
                            </span>
                            {getStatusBadge(lang.TranslationStatusId)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
            );
          })}
            {fundCards.length === 0 && !loading && (
              <div style={{ padding: 20, color: '#888' }}>No translation data found</div>
            )}
          </div>
        ) : (
          <div className={`ag-theme-balham ${classes.gridContainer}`}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredNarratives}
              columnDefs={narrativeColumnDefs}
              domLayout="normal"
              pagination={true}
              paginationPageSize={20}
              rowHeight={80}
              defaultColDef={{ sortable: true, resizable: true }}
              animateRows={false}
              getRowId={(params) => String(params.data.BaseNarrativeId || params.data.NarrativeId)}
              rowSelection="multiple"
              suppressRowClickSelection
              onSelectionChanged={(e) => {
                const selected = e.api.getSelectedRows();
                setSelectedNarrativeIds(new Set(selected.map((r) => r.NarrativeId || r.BaseNarrativeId)));
              }}
              onFirstDataRendered={(params) => {
                setTimeout(() => params.api.redrawRows(), 0);
              }}
            />
          </div>
        )}
      </div>

      {/* Change Status Modal */}
      {activeModal === 'changeStatus' && (
        <div className={classes.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>Change Translation Status</div>
            <div className={classes.formGroup}>
              <label>New Status</label>
              <select value={changeStatusValue} onChange={(e) => setChangeStatusValue(e.target.value)}>
                <option value="">-- Select --</option>
                {translationStatuses.map((s) => (
                  <option key={s.TranslationStatusId} value={s.TranslationStatusId}>
                    {s.DisplayName}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.modalActions}>
              <GrpButton size="Small" type="Primary" text="Apply" onClick={handleChangeStatus} />
              <GrpButton size="Small" type="Secondary" text="Cancel" onClick={() => setActiveModal(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Translation;
